<template>
    <docu-layout title="Contact Us" :show-large-footer="false">
        <div class="bg-surface-0 px-6 md:px-12 lg:px-20 dark:bg-surface-950">
            <div
                class="flex flex-col items-start gap-10 rounded-3xl bg-surface-50 p-10 lg:flex-row lg:gap-[4.5rem] lg:p-14 dark:bg-surface-900"
            >
                <div class="flex flex-1 flex-col justify-between lg:min-h-[25rem]">
                    <div>
                        <h2 class="text-3xl font-bold text-surface-900 lg:text-4xl dark:text-surface-0">
                            Get in Touch.
                        </h2>
                        <p class="mt-6 text-lg leading-6 text-surface-600 lg:text-xl dark:text-surface-400">
                            Looking for a demo or more information? Send us a message and we'll schedule a discovery
                            call to show you what Docu-Vault can do for your business.
                        </p>
                    </div>
                    <div class="mt-5">
                        <div class="text-base font-medium leading-6 text-surface-900 lg:text-xl dark:text-surface-0">
                            <div>Call us at {{ Ajility.CompanyPhoneNumber }}</div>
                            <div>Email us at hello@docu-vault.com</div>
                        </div>
                        <div class="mt-3.5 flex items-center gap-3">
                            <a
                                v-for="(social, index) in AjilitySocialMedia.data"
                                :key="index"
                                :href="social.socialLink"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="flex h-9 w-9 items-center justify-center rounded-full border border-surface-300 bg-surface-0 text-base text-surface-900 transition-all hover:bg-surface-100 lg:text-lg dark:border-surface-600 dark:bg-surface-900 dark:text-surface-0 dark:hover:bg-surface-800"
                            >
                                <img :src="social.icon" :alt="social.title" class="h-6 w-6" />
                            </a>
                        </div>
                    </div>
                </div>
                <form class="flex w-full flex-1 flex-col gap-4 lg:gap-3" @submit.prevent="submitForm">
                    <div class="mb-2">
                        <InputText
                            v-model="form.name"
                            id="name"
                            type="text"
                            placeholder="Name"
                            class="border-gray-300 bg-white"
                            autocomplete="name"
                        />
                    </div>
                    <div class="mb-2">
                        <InputText
                            placeholder="Email"
                            v-model="form.email"
                            id="email"
                            type="text"
                            class="border-gray-300 bg-white"
                            autocomplete="email"
                        />
                        <InputText v-model="form.hp_email" type="text" name="hp_email" style="display: none" />
                    </div>
                    <div class="mb-2">
                        <Textarea
                            v-model="form.message"
                            id="message"
                            type="text"
                            class="w-full border-gray-300 bg-white"
                            rows="8"
                            placeholder="Message"
                            autocomplete="off"
                            auto-resize
                        />
                    </div>
                    <Button label="Submit" type="submit" class="w-full" :disabled="!isValid" />
                </form>
            </div>
            <div class="align-content-center flex flex-wrap">
                <div>
                    <img
                        alt="Docu-Vault Office Location on Map"
                        :src="mapOfficeLocationImg"
                        class="min-h-[20rem] object-cover"
                    />
                </div>
                <div
                    class="large:mt-6 large:text-lg medium:text-md p-4 leading-6 text-surface-600 dark:text-surface-400"
                >
                    <h2 class="text-medium font-bold text-surface-900 lg:text-4xl dark:text-surface-0">Snail Mail.</h2>
                    <address class="mt-4" style="font-style: normal !important">
                        {{ Ajility.ParentCompany }} (DBA {{ Ajility.DBAName }})<br />
                        {{ Ajility.Address }} {{ Ajility.Address2 }}<br />
                        {{ Ajility.City }}, {{ Ajility.State }} {{ Ajility.PostalCode }}
                    </address>
                </div>
            </div>
        </div>
    </docu-layout>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import axios from 'axios'
import Button from 'primevue/button'
import Textarea from 'primevue/textarea'
import InputText from 'primevue/inputtext'
import DocuLayout from '@/Layouts/DocuLayout.vue'
import { useToast } from 'primevue/usetoast'
import { useHead } from '@vueuse/head'
import { schemaTagOptions } from '@/Composables/SchemaTagOptions'
import { Ajility, AjilitySocialMedia } from '@/Enums/Ajility'
import { useForm } from '@inertiajs/vue3'
import mapOfficeLocationImg from '@/assets/map-office-location.webp'

const toast = useToast()
const form = useForm({
    email: '',
    name: '',
    message: '',
    hp_email: '',
})

const isValid = computed((): boolean => {
    return (
        form.name !== '' &&
        form.email !== '' &&
        form.email.includes('@') &&
        form.email.includes('.') &&
        form.message !== ''
    )
})

async function submitForm() {
    try {
        await axios.post('/send-contact', form)
        toast.add({
            severity: 'success',
            summary: 'Message sent',
            detail: 'Your email has been sent! Please allow 24 hours for a team member to reach out.',
            life: 14000,
        })

        form.email = ''
        form.name = ''
        form.message = ''
        form.hp_email = ''
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
        if (error.response && error.response.status === 422) {
            toast.add({
                severity: 'error',
                summary: 'Something went wrong',
                detail: 'Please check the form for missing information and a valid email address.',
                life: 7000,
            })
        } else {
            toast.add({
                severity: 'error',
                summary: 'Something went wrong',
                detail: 'Something is up with our email provider. We were automatically notified of the issue. Please try again later.',
                life: 7000,
            })
        }
    }
}

useHead(
    schemaTagOptions({
        title: 'Contact Us | Docu-Vault',
        description:
            'Get in touch with Docu-Vault for expert document management solutions. Contact us for secure file storage, document revisions, QR code access, and more.',
        schemaType: 'ContactPage',
    }),
)
</script>
